import throttle from "lodash/throttle";
import React, { JSX, useCallback, useEffect, useRef } from "react";

interface InfiniteScrollProps {
	onEndReached: () => void;
	loading: boolean;
	children?: React.ReactNode | JSX.Element;
	scrollingElement?: React.RefObject<HTMLElement | null>;
}
export const InfiniteScroll: React.FC<InfiniteScrollProps> = ({
	onEndReached,
	scrollingElement,
	loading,
	children,
}) => {
	const sentinel = useRef<HTMLDivElement>(null);

	const handler = useCallback(
		throttle(
			() => {
				if (!sentinel.current || (scrollingElement && !scrollingElement.current)) {
					return;
				}
				if (loading) {
					return;
				}

				const sentinelTop = sentinel.current.getBoundingClientRect().top;
				let endReached;
				if (scrollingElement) {
					const scrollingRect = scrollingElement.current!.getBoundingClientRect();
					endReached = sentinelTop - scrollingRect.top < scrollingRect.height * 2;
				} else {
					endReached = sentinelTop - window.innerHeight < 1200;
				}
				if (endReached) {
					onEndReached();
				}
			},
			500,
			{ leading: true, trailing: true }
		),
		[onEndReached, scrollingElement, loading]
	);

	const memoizedHandler = useRef(handler);

	useEffect(() => {
		memoizedHandler.current = handler;
	}, [handler]);

	useEffect(() => {
		const listener = scrollingElement?.current ?? window;

		listener.addEventListener("scroll", handler, true);
		listener.addEventListener("resize", handler);

		return () => {
			handler.cancel();

			listener.removeEventListener("scroll", handler, true);
			listener.removeEventListener("resize", handler);
		};
	}, [handler, scrollingElement]);

	useEffect(() => {
		if (!loading) {
			memoizedHandler.current();
		}
	}, [loading]);

	return (
		<>
			{children}
			<div ref={sentinel}></div>
		</>
	);
};
